import React, { useEffect, useState } from 'react'
import { Label } from '../../components/ui/label'
import { Combobox } from '../../components/ui/combobox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useQuery } from '@tanstack/react-query';
import axios from '../../lib/axios';
import { Toaster } from 'react-hot-toast';
import useAuth from '../../hooks/useAuth';
import empty from '../../assets/images/illustrations/empty-cycling.svg'
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '../../components/ui/carousel';
import EmblaCarousel from 'embla-carousel'
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Link, useSearchParams } from 'react-router-dom';
import { Bike } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import Footer from '../../layouts/Footer';
import { getTranslationString } from '../../lib/translation';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import * as Icon from "lucide-react";
import Top3 from '../../assets/icons/top-3.png'
import Top5 from '../../assets/icons/top-5.png'

interface RankingProps {
    competition?: string;
    prono?: string;
    firstProno?: string | null;
    pronoAmount?: number;
    showChangePoints?: boolean;
    showPositionChanges?: boolean;
}

type PerfectPredictionBadgeType = 'top3' | 'top5' | 'default';

interface PerfectPredictionBadge {
    type: PerfectPredictionBadgeType;
    count: number;
}

const getPerfectPredictionBadgeTypes = (extraInfo?: string): PerfectPredictionBadge[] => {
    const normalizedExtraInfo = (extraInfo || '').toUpperCase();
    const top3Count = normalizedExtraInfo.split('BONUS_TOP_3').length - 1;
    const top5Count = normalizedExtraInfo.split('BONUS_TOP_5').length - 1;
    const badgeTypes: PerfectPredictionBadge[] = [];

    if (top3Count > 0) {
        badgeTypes.push({ type: 'top3', count: top3Count });
    }
    if (top5Count > 0) {
        badgeTypes.push({ type: 'top5', count: top5Count });
    }
    if (badgeTypes.length === 0) {
        badgeTypes.push({ type: 'default', count: 1 });
    }
    return badgeTypes;
}

const RankingCompany = ({competition, prono, firstProno, pronoAmount, showChangePoints, showPositionChanges}:RankingProps) => {
    const {t} = useTranslation()
    const {auth} = useAuth()

    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState(searchParams.get('name') || '');

    useEffect(() => {
        if (query) {
            setSearchParams({ name:query });
        } else {
            setSearchParams({});
        }
    }, [query, setSearchParams]);

    const {data:ranking, isFetching} = useQuery<EventRanking[] | null>({
        queryKey: [`ranking_company_${competition}_${prono}`],
        queryFn: async () => {
            try {
                let url = `ranking?eventid=${competition}&partnerid=${auth?.data?.m_oPartner?.m_iPartnerID}`
                const pronoId = prono?.toString().trim();
                if (pronoId && pronoId !== "-1") {
                    url = `ranking?pronoid=${prono}&partnerid=${auth?.data?.m_oPartner?.m_iPartnerID}`
                }
                const {data} = await axios.get(url)
                if(Array.isArray(data)){                         
                    return data
                }
                return null
            } catch (error) {
                console.log(error)
                return null
            }
        },
        enabled: !!competition
    })

    return (
        <>
        {(!ranking && !isFetching && competition) 
            ? <div className='flex flex-col items-center justify-center'>
                <img className='max-w-xs mb-5' src={empty} alt="empty" />
                <p className='text-lg font-semibold text-neutral-500'>{t("ranking.no_ranking_title")}</p>
                <p className='text-sm text-neutral-400'>{t("ranking.no_ranking_desc")}</p>
            </div> 
            :
            <div className='flex flex-col w-full gap-4'>
            {ranking && ranking.length > 0 && 
            <div className='flex items-center gap-2'>
                <Input 
                    startEnhancer={<Icon.Search className='w-4 h-4 opacity-50'/>}
                    placeholder={t("ranking.search_player")}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>
            }

            <Table className='border rounded-md'>
                <TableHeader className='bg-slate-200'>
                    <TableRow className='bg-slate-200'>
                        {prono !== firstProno && pronoAmount > 1 && showPositionChanges && <TableHead className='w-8'></TableHead>}
                        <TableHead className='px-2 py-4 sm:px-4 w-14'>{t("ranking.position")}</TableHead>
                        <TableHead className='max-w-[100px] py-4 px-0 sm:px-4'>{t("ranking.name")}</TableHead>
                        <TableHead className='hidden px-2 py-4 sm:block sm:px-4'>{t("ranking.group")}</TableHead>
                        <TableHead className='w-16 px-2 py-4 text-right sm:px-4'>{t("ranking.points")}</TableHead>
                        <TableHead className='w-8'></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!isFetching ? 
                    (ranking && 
                        ranking
                            .filter(i => i.m_sUser.toLowerCase().includes(query.toLowerCase()))
                            .map((rank,i)=>{
                                const pos = rank.m_iPositionInPartner;
                                return (
                                    <TableRow className={`${pos===1&&'font-semibold'} ${rank.m_iUserID === auth?.data?.m_iUserID && '!text-green-700 font-semibold'} bg-tifo-darker hover:bg-tifo-light/10`} key={i}>
                                        {prono !== firstProno && pronoAmount > 1 && showPositionChanges &&
                                        <TableCell>
                                            <div className='relative flex flex-col items-center justify-center'>
                                                {prono !== firstProno && rank.m_iChangePositionPartner > 0  && <Icon.ArrowUp className=' w-3 h-3 text-emerald-500' />}
                                                {prono !== firstProno && rank.m_iChangePositionPartner < 0 && <Icon.ArrowDown className=' w-3 h-3 text-red-500' />}
                                                {prono !== firstProno && rank.m_iChangePositionPartner === 0 && <Icon.Equal className='w-3 h-3 text-blue-400'/>}
                                                <p
                                                    className={cn(
                                                        prono !== firstProno && rank.m_iChangePositionPartner > 0
                                                            ? 'text-emerald-500'
                                                            : prono !== firstProno && rank.m_iChangePositionPartner < 0
                                                                ? 'text-red-500'
                                                                : prono !== firstProno && rank.m_iChangePositionPartner === 0
                                                                    ? 'text-blue-400'
                                                                    : 'text-neutral-400'
                                                    )}
                                                >
                                                    {!(rank.m_iChangePositionPartner === 0) ? rank.m_iChangePositionPartner : ""}
                                                </p>
                                            </div>
                                        </TableCell>
                                        }
                                        <TableCell className='px-2 sm:py-6 sm:px-4'>
                                            {pos}
                                        </TableCell>
                                        <TableCell className='max-w-[100px] sm:py-6 px-0 sm:px-4 whitespace-nowrap'>
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="truncate font-medium">
                                                    {rank.m_sUser || "Anoniempje"}
                                                    {rank.m_iUserID === auth?.data?.m_iUserID && (
                                                    <span className="ml-1 text-sm text-muted-foreground">
                                                        ({t("ranking.you")})
                                                    </span>
                                                    )}
                                                </span>
                                                {(rank.m_iPerfectPrediction ?? 0) > 0 && (
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        {getPerfectPredictionBadgeTypes(rank.m_sExtraInfo).map((badge, idx) => (
                                                            <Badge
                                                                key={`${rank.m_iUserID}-${badge.type}-${idx}`}
                                                                variant="outline"
                                                                className="text-tifo text-xs px-2 py-0.5 flex items-center gap-1 rounded-full whitespace-nowrap shrink-0"
                                                                title={String(t("ranking.perfect_prediction"))}
                                                            >
                                                                {badge.type === 'top3' && <img src={Top3} className='w-4 h-4' alt='top 3 bonus' />}
                                                                {badge.type === 'top5' && <img src={Top5} className='w-4 h-4' alt='top 5 bonus' />}
                                                                {badge.type === 'default' && <Icon.BadgeCheck className="w-4 h-4" />}
                                                                {prono && prono === "-1" ? badge.count : ""}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}

                                            </div>
                                        </TableCell>
                                        <TableCell className='hidden px-2 sm:block sm:py-6 sm:px-4'>{rank.m_sDepartment}</TableCell>
                                        <TableCell className='px-2 text-right sm:py-6 sm:px-4'>
                                            <p className='whitespace-nowrap'>
                                                {rank.m_iPoints}
                                            </p>
                                        </TableCell>
                                        <TableCell>
                                            {showChangePoints && rank.m_iChangePoints > 0
                                                ? <div className="relative flex flex-col items-center justify-center gap-0 text-emerald-500">
                                                    <Icon.ArrowUp className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full left-1/2' />
                                                    <p className='text-xs'>+{rank.m_iChangePoints}</p>
                                                </div>
                                                : null
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                    })) 
                    :
                    <TableRow><TableCell colSpan={99}>{t("ranking.loading")}</TableCell></TableRow>
                    }              
                </TableBody>
            </Table>
            </div>
        }</>
    )
}

const RankingDepartment = ({competition, prono, firstProno, pronoAmount, showChangePoints, showPositionChanges}:RankingProps) => {
    const {t} = useTranslation()
    const {auth} = useAuth()
    const {data:ranking, isFetching} = useQuery<EventRanking[] | null>({
        queryKey: [`ranking_group_${competition}_${prono}`],
        queryFn: async () => {
            try {
                let url = `ranking?eventid=${competition}&departmentid=${auth.data.m_oDepartment.m_iDepartmentID}`
                const pronoId = prono?.toString().trim();
                if (pronoId && pronoId !== "-1") {
                    url = `ranking?pronoid=${prono}&departmentid=${auth.data.m_oDepartment.m_iDepartmentID}`
                }
                const {data} = await axios.get(url)
                if(Array.isArray(data)){ return data }
                return null
            } catch (error) {
                console.log(error)
                return null
            }
        },
        enabled: !!competition
    })

    return (
        <>{(!ranking && !isFetching && competition) 
            ? <div className='flex flex-col items-center justify-center'>
                <img className='max-w-xs mb-5' src={empty} alt="empty" />
                <p className='text-lg font-semibold text-neutral-500'>{t("ranking.no_ranking_title")}</p>
                <p className='text-sm text-neutral-400'>{t("ranking.no_ranking_desc")}</p>
            </div> 
            :
            <Table className='border rounded-md'>
                <TableHeader className='bg-slate-200'>
                    <TableRow className='bg-slate-200'>
                        {prono !== firstProno && showPositionChanges && <TableHead className='w-8'></TableHead>}
                        <TableHead className='py-4'>{t("ranking.position")}</TableHead>
                        <TableHead className='py-4'>{t("ranking.name")}</TableHead>
                        <TableHead className='py-4'>{t("ranking.group")}</TableHead>
                        <TableHead className='py-4'>{t("ranking.points")}</TableHead>
                        <TableHead className='w-8'></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!isFetching ? (ranking && ranking.map((rank,i)=>{
                        return (
                            <TableRow className={`${i===0&&'font-semibold'} ${rank.m_iUserID === auth?.data?.m_iUserID && '!text-green-700 font-semibold'} bg-tifo-darker hover:bg-tifo-light/10`} key={i}>
                                {prono !== firstProno && pronoAmount > 1 && showPositionChanges &&
                                  <TableCell>
                                          <div className='relative flex flex-col items-center justify-center'>
                                            {prono !== firstProno && rank.m_iChangePositionDepartment > 0 ? <Icon.ArrowUp className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full text-emerald-500 left-1/2' /> : null}
                                            {prono !== firstProno && rank.m_iChangePositionDepartment < 0 ? <Icon.ArrowDown className='absolute bottom-0 w-3 h-3 text-red-500 -translate-x-1/2 translate-y-full left-1/2' /> : null}
                                            {prono !== firstProno && rank.m_iChangePositionDepartment === 0 ? <Icon.Equal className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full text-cyan-200 left-1/2' /> : null}
                                            <p
                                                className={cn(
                                                    prono !== firstProno && rank.m_iChangePositionDepartment > 0
                                                        ? 'text-emerald-500' :
                                                        prono !== firstProno && rank.m_iChangePositionDepartment < 0
                                                            ? 'text-red-500'
                                                            : 'text-neutral-400'
                                                )}
                                            >
                                                {rank.m_iChangePositionDepartment}
                                            </p>
                                          </div>
                                  </TableCell>
                                }
                                <TableCell className='sm:py-6'>{rank.m_iPositionInDepartment}</TableCell>
                                <TableCell className='sm:py-6 whitespace-nowrap'>
                                    <div className='flex items-center gap-2 min-w-0'>
                                        <span className='truncate'>
                                            {rank.m_sUser} {rank.m_iUserID === auth?.data?.m_iUserID && `(${t("ranking.you")})`}
                                        </span>
                                        {(rank.m_iPerfectPrediction ?? 0) > 0 && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                {getPerfectPredictionBadgeTypes(rank.m_sExtraInfo).map((badge, idx) => (
                                                    <Badge
                                                        key={`${rank.m_iUserID}-${badge.type}-${idx}`}
                                                        variant="secondary"
                                                        className="bg-tifo-light text-tifo text-xs px-2 py-0.5 flex items-center gap-1 rounded-full whitespace-nowrap shrink-0"
                                                        title={String(t("ranking.perfect_prediction"))}
                                                    >
                                                        {badge.type === 'top3' && <img src={Top3} className='w-4 h-4' alt='top 3 bonus' />}
                                                        {badge.type === 'top5' && <img src={Top5} className='w-4 h-4' alt='top 5 bonus' />}
                                                        {badge.type === 'default' && <Icon.BadgeCheck className="w-4 h-4" />}
                                                        {prono && prono === "-1" ? badge.count : ""}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className='sm:py-6'>{rank.m_sDepartment}</TableCell>
                                <TableCell className='sm:py-6'>
                                    <p className='whitespace-nowrap'>
                                        {rank.m_iPoints}
                                    </p>
                                </TableCell>
                                <TableCell>
                                    {showChangePoints && rank.m_iChangePoints > 0
                                        ? <div className="relative flex flex-col items-center justify-center gap-0 text-emerald-500">
                                            <Icon.ArrowUp className='absolute top-0 w-3 h-3 -translate-x-1/2 -translate-y-full left-1/2' />
                                            <p className='text-xs'>+{rank.m_iChangePoints}</p>
                                        </div>
                                        : null
                                    }
                                </TableCell>
                            </TableRow>
                        )
                    })) 
                    :
                    <TableRow><TableCell colSpan={99}>{t("ranking.loading")}</TableCell></TableRow>
                    }              
                </TableBody>
            </Table>
        }</>
    )
}

const RankingGroups = ({competition, prono, firstProno, pronoAmount}:RankingProps) => {
    const {t} = useTranslation()
    const {auth} = useAuth()

    const {data:ranking, isFetching} = useQuery<EventRanking[] | null>({
        queryKey: [`ranking_groups_${competition}_${prono}`],
        queryFn: async () => {
            try {
                let url = `ranking?eventid=${competition}&partnerid=${auth?.data?.m_oPartner?.m_iPartnerID}&type=depts`
                const pronoId = prono?.toString().trim();
                if (pronoId && pronoId !== "-1") {
                    url = `ranking?pronoid=${prono}&partnerid=${auth?.data?.m_oPartner?.m_iPartnerID}&type=depts`
                }
                const { data } = await axios.get(url);

                return data;
            } catch (error) {
                console.log(error);
                return null;
            }
        },
        enabled: !!competition
    })

    return (
        <>{(!ranking && !isFetching && competition) 
            ? <div className='flex flex-col items-center justify-center'>
                <img className='max-w-xs mb-5' src={empty} alt="empty" />
                <p className='text-lg font-semibold text-neutral-500'>{t("ranking.no_ranking_title")}</p>
                <p className='text-sm text-neutral-400'>{t("ranking.no_ranking_desc")}</p>
            </div> 
            :
            <Table className='border rounded-md'>
                <TableHeader className='bg-slate-200'>
                    <TableRow className='bg-slate-200'>
                        <TableHead className='py-4'>{t("ranking.position")}</TableHead>
                        <TableHead className='py-4'>{t("ranking.name")}</TableHead>
                        <TableHead className='py-4'>{t("ranking.average")}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!isFetching ? (ranking && ranking.map((rank,i)=>{
                        return (
                            <TableRow className={`${rank.m_iPositionInPartner===0&&'font-semibold'} ${(rank?.m_iDepartmentID === auth?.data?.m_oDepartment?.m_iDepartmentID) && '!text-green-700 font-semibold'} bg-tifo-darker hover:bg-tifo-light/10`} key={i}>
                                <TableCell className='sm:py-6'>{rank.m_iPositionInPartner}</TableCell>
                                <TableCell className='sm:py-6 whitespace-nowrap'>{rank.m_sDepartment}</TableCell>
                                <TableCell className='sm:py-6'>
                                    <p className='whitespace-nowrap'>
                                        {rank.m_nDeptAvgPoints}
                                    </p>
                                </TableCell>
                            </TableRow>
                        )
                    })) 
                    :
                    <TableRow><TableCell colSpan={99}>{t("ranking.loading")}</TableCell></TableRow>
                    }              
                </TableBody>
            </Table>
        }</>
    )
}

const Ranking = () => {
    const {auth} = useAuth()
    const {t, i18n} = useTranslation()
    const [selected_type, setselected_type] = useState('company');
    const [selected_competition, setselected_competition] = useState<string>();
    const [selected_prono, setselected_prono] = useState<string | undefined>("-1");
    const [pronoAmount, setPronoAmount] = useState<number>()

    const { data:departments } = useQuery({
        queryKey: [`departments`],
        queryFn: async () => {
            const { data } = await axios.get(`departments?partnerid=${auth.data.m_oPartner.m_iPartnerID}`)
            if(!Array.isArray(data)){throw new Error('No array')}
            return data;
        }
    })

    const { data } = useQuery<PartnerEvent[] | null>({
        queryKey: [`events`],
        queryFn: async () => {
          try {
            const {data} = await axios.get(`partners/${auth.data.m_oPartner.m_iPartnerID}/events`)
            if(!Array.isArray(data)){throw new Error('No array')}
            let result = data.filter(i=>i.m_bActive===true).reverse()
            return result
          } catch (error) {
            console.log(error);
            return null
          }
        },
        enabled: !!auth.data
    })

    useEffect(() => {
        if (!selected_competition && data?.length === 1) {
            setselected_competition(data[0].m_oEvent.m_iEventID.toString())
        }
    }, [data, selected_competition])

    const { data:pronos } = useQuery<Prono[] | null>({
        queryKey: [`pronos-${selected_competition}`],
        queryFn: async () => {
          try {
            const { data } = await axios.get(`pronos?eventid=${selected_competition}`)
            if(!Array.isArray(data)){throw new Error('No array')}
            return data
          } catch (error) {
            console.log(error);
            return null
          }
        },
        enabled: !!selected_competition
    })

    EmblaCarousel.globalOptions = { watchDrag: false }
    const [api, setApi] = useState<CarouselApi>()

    const latestOpenEvent = data ? data.find(i=>i.m_oEvent.m_bClosed === false) : undefined
    const language = i18n.language.toUpperCase() as Language;
    const eventOptions = data?.map(event => ({ value: event.m_oEvent.m_iEventID, label: getTranslationString(language, event.m_oEvent, 'm_sNameNL') || event.m_oEvent.m_sNameNL })) || [];
    let pronoOptions = pronos?.map(prono => ({value: prono.m_iPronoID,label: getTranslationString(language, prono, 'm_sDescriptionNL') || prono.m_sDescriptionNL})) || [];
    const firstProno = pronoOptions[0]?.value?.toString();

    useEffect(() => {
        if (pronos) {
            const closedPronoAmount = pronos.filter((prono) => prono.m_bClosed).length;
            setPronoAmount(closedPronoAmount)
            return;
        }
        setPronoAmount(0)
    }, [pronos])


    const emptyOption = {value: -1, label: "Alle"};
    pronoOptions.unshift(emptyOption)
    
    const isPhoneScreen = window.innerWidth < 768;
    const latestClosedPronoId = pronos
        ?.filter((prono) => prono.m_bClosed)
        .at(-1)
        ?.m_iPronoID
        ?.toString();

    const isGeneralRanking = selected_prono === "-1";
    const isLastRideRanking = !!latestClosedPronoId && selected_prono === latestClosedPronoId;

    const showChangePoints = isPhoneScreen
        ? false
        : isGeneralRanking;
    const showPositionChanges = isPhoneScreen
        ? isGeneralRanking
        : (isGeneralRanking || isLastRideRanking);

    return (<><Toaster />
    <div className='w-full h-[88px] bg-tifo'></div>
        <div className='relative py-16 group'>
            <div className='relative z-[1] max-w-5xl mx-auto px-4 flex flex-col gap-4 text-white'>
                <h2 className='text-3xl font-bold md:text-6xl'>{t("ranking.cta")}</h2>
                <p className='text-lg'>{t("ranking.cta_description")}</p>
                {latestOpenEvent && 
                <Link to={(data && latestOpenEvent.m_oEvent && latestOpenEvent?.m_oEvent?.m_sUrlNL) ? latestOpenEvent?.m_oEvent?.m_sUrlNL : ''} className='mt-4 w-fit'>
                    <Button variant='tifo' className='gap-2'>
                        {t("ranking.cta_button")} <Bike className='w-4 h-4'/>
                    </Button>
                </Link>}
            </div>
            <div className='absolute top-0 left-0 w-full h-full overflow-hidden bg-tifo'>
                {data && latestOpenEvent && latestOpenEvent.m_oEvent && latestOpenEvent.m_oEvent.m_sImageUrl && <img src={latestOpenEvent.m_oEvent.m_sImageUrl} alt={latestOpenEvent.m_oEvent.m_sNameNL} className='object-cover w-full h-full duration-1000 opacity-20 group-hover:scale-105'/>}
            </div>
        </div>
        <div className='flex flex-col items-center bg-slate-50'> 
        <div className='flex flex-col w-full max-w-5xl gap-4 px-4 py-8 sm:py-16'>
            <h1 className='max-w-4xl text-2xl font-bold leading-tight sm:text-4xl md:mb-4'>{t("ranking.title")}</h1>

            <div className='flex flex-wrap-reverse items-center justify-between gap-4 p-2 bg-white rounded-lg shadow-lg sm:p-8'>
                <div className='flex flex-wrap items-center gap-3'>
                    <div className='flex flex-col gap-1'>
                        <Label className='ml-2 font-semibold uppercase text-neutral-600' htmlFor='competition'>{t("ranking.event")}</Label>
                        <Combobox selectedValue={selected_competition} onValueChange={(v)=>{setselected_prono("-1"); setselected_competition(v.value.toString())}} options={eventOptions} className='min-w-[250px] rounded-full' placeholder={t("ranking.event_placeholder")} input_placeholder={t("ranking.search_placeholder")} id='competition'/>
                    </div>
                    
                    {pronos && 
                    <div className='flex flex-col gap-1'>
                        <Label className='ml-2 font-semibold uppercase text-neutral-600' htmlFor='prono'>{t("ranking.competition")}</Label>
                        <Combobox 
							selectedValue={selected_prono} 
							onValueChange={(v)=>{
								if(selected_prono?.toString() === v.value?.toString()) {
									setselected_prono("-1")
									return;
								}
								setselected_prono(v.value.toString())
							}} 
							options={pronoOptions} 
							className='min-w-[250px] rounded-full' 
							placeholder={t("ranking.competition_placeholder")} 
							id='prono'
						/>
                    </div>
                    }
                </div>

                <div className={cn('grid items-center gap-2 bg-white border h-10 rounded-full p-1 backdrop-blur-sm',
                    departments?.length > 1 ? 'grid-cols-3' : 'grid-cols-1'
                )}>
                    <button onClick={()=>{api.scrollTo(0); setselected_type('company') }} className={`${selected_type === 'company' && '!bg-tifo-light !text-tifo'} flex justify-center items-center gap-2 text-tifo text-sm font-medium h-full rounded-full px-4 hover:bg-neutral-100 duration-200`}>{t("ranking.company")}</button>
                    {(departments?.length > 1) && <button onClick={()=>{api.scrollTo(1); setselected_type('department') }} className={`${selected_type === 'department' && '!bg-tifo-light !text-tifo'} flex justify-center items-center gap-2 text-tifo text-sm font-medium h-full rounded-full px-4 hover:bg-neutral-100 duration-200`}>{t("ranking.department")}</button>}
                    {(departments?.length > 1) && <button onClick={()=>{api.scrollTo(2); setselected_type('groups') }} className={`${selected_type === 'groups' && '!bg-tifo-light !text-tifo'} flex justify-center items-center gap-2 text-tifo text-sm font-medium h-full rounded-full px-4 hover:bg-neutral-100 duration-200`}>{t("ranking.groups")}</button>}
                </div>
            </div>

            <Carousel setApi={setApi} opts={{watchDrag:false}}>
                <CarouselContent>
                    <CarouselItem>
                        <RankingCompany competition={selected_competition} prono={selected_prono} firstProno={firstProno} pronoAmount={pronoAmount} showChangePoints={showChangePoints} showPositionChanges={showPositionChanges}/>
                    </CarouselItem>
                    <CarouselItem>
                        <RankingDepartment competition={selected_competition} prono={selected_prono} firstProno={firstProno} pronoAmount={pronoAmount} showChangePoints={showChangePoints} showPositionChanges={showPositionChanges}/>
                    </CarouselItem>
                    <CarouselItem>
                        <RankingGroups competition={selected_competition} prono={selected_prono} firstProno={firstProno} pronoAmount={pronoAmount}/>
                    </CarouselItem>
                </CarouselContent>
            </Carousel>
            
        </div>
    </div>

    <Footer />
    </>
  )
}

export default Ranking
